import { Autocomplete, TextField } from "@mui/material";
import classNames from "classnames";
import isEqual from "lodash/isEqual";
import * as React from "react";

import { useFileIdToTopics } from "@/shared/components/visualization/WorkspaceCtx/hooks";
import { useVizDispatch } from "@/shared/state/visualization";
import { updateThreeDEntity } from "@/shared/state/visualization/actions";
import { Entity } from "@/shared/state/visualization/schema/v1/three-d";

import styles from "../EntityConfig.module.css";

import { positionFor } from "./positionForPreset";
import { PositionPreset, presetFor, presets } from "./presets";
import { useWorkspacePositionPresets } from "./useWorkspacePositionPresets";

/**
 * The preset field of a 3D entity's position settings, where a user picks the
 * coordinate convention and the topic data each axis plots.
 *
 * When no preset matches the entity's current position, the list also offers
 * that position as a "Custom (...)" entry, so the field shows what the entity
 * actually plots instead of sitting blank.
 *
 * Picking an entry replaces the position whole: an axis the picked preset
 * leaves unbound plots 0 rather than keeping the data the entity had.
 */
export function PositionPresetSelector(props: {
  panelId: string;
  entity: Entity;
}) {
  const { panelId, entity } = props;
  const dispatch = useVizDispatch();

  const fileIdToTopics = useFileIdToTopics();

  const prefilled = useWorkspacePositionPresets();
  const [options, currentPreset] = React.useMemo(
    function optionsForPresetsAndCurrentState() {
      const options: PositionPreset[] = [...prefilled];

      function isPositionPreset(preset: PositionPreset) {
        return isEqual(positionFor(preset, fileIdToTopics), entity.position);
      }
      const defaults = Object.values(presets);
      let currentPreset =
        prefilled.find(isPositionPreset) || defaults.find(isPositionPreset);
      // Neither a workspace preset nor a built-in default matches the entity's
      // current position.
      if (!currentPreset) {
        const customPreset = presetFor(entity.position);
        if (customPreset) {
          options.push(customPreset);
        }
        currentPreset = customPreset;
      }

      options.push(...defaults);
      return [options, currentPreset];
    },
    [entity.position, prefilled, fileIdToTopics],
  );

  return (
    <Autocomplete
      freeSolo
      size="small"
      fullWidth
      options={options}
      getOptionLabel={(option) =>
        typeof option === "string"
          ? option
          : `${option.name.title} ${option.name.subtitle}`
      }
      renderOption={(renderOptionProps, option: PositionPreset) => (
        <li {...renderOptionProps}>
          <p className={styles.input}>
            <span className={styles.highlight}>{option.name.title}</span>
            {option.name.formatHint && <i> {`(${option.name.formatHint})`}</i>}
            <br />
            <i>{option.name.subtitle}</i>
          </p>
        </li>
      )}
      isOptionEqualToValue={(option, value) => isEqual(option, value)}
      onChange={(_, value) => {
        if (!!value && typeof value === "object") {
          const presetPosition = positionFor(value, fileIdToTopics);
          dispatch(
            updateThreeDEntity(panelId, entity.id, {
              position: presetPosition,
            }),
          );
        }
      }}
      value={currentPreset ?? ""}
      className={classNames([styles.input, styles.field])}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Preset"
          InputProps={{ ...params.InputProps, className: styles.input }}
          InputLabelProps={{
            ...params.InputLabelProps,
            shrink: true,
            className: styles.input,
          }}
        />
      )}
    />
  );
}
